import React, { useState } from 'react';
import { Container, Modal, Row, Col, Form, Card } from 'react-bootstrap';
import { useTheme } from "../../context/ThemeContext";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";

// Şirket veri tipi tanımı
interface Company {
  id: number;
  name: string;
  industry: string;
  size: string;
  location: string;
  website: string;
  revenue: string;
  employees: number;
  founded: number;
  description: string;
  competitors: string[];
  marketShare: number;
  growth: number;
}

// Pazar analizi veri tipi tanımı
interface MarketMetric {
  title: string;
  value: string | number | string[];
  trend: number;
  color: string;
  icon: string;
}

// Örnek şirket verileri
const dummyCompanies: Company[] = [
  {
    id: 1,
    name: 'Tech Solutions Inc.',
    industry: 'Technology',
    size: '500-1000',
    location: 'San Francisco, CA',
    website: 'www.techsolutions.com',
    revenue: '$50M - $100M',
    employees: 750,
    founded: 2010,
    description: 'Leading provider of enterprise software solutions.',
    competitors: ['Innovation Labs', 'Global Tech', 'Future Systems'],
    marketShare: 15,
    growth: 25,
  },
  {
    id: 2,
    name: 'Global Industries',
    industry: 'Manufacturing',
    size: '1000-5000',
    location: 'New York, NY',
    website: 'www.globalind.com',
    revenue: '$100M - $500M',
    employees: 2500,
    founded: 1995,
    description: 'Global manufacturing and distribution company.',
    competitors: ['Industrial Corp', 'Manufacturing Pro', 'Global Tech'],
    marketShare: 22,
    growth: 18,
  },
];

// Pazar analizi verileri
const marketAnalysis: MarketMetric[] = [
  {
    title: 'Total Companies',
    value: 150,
    trend: 12.5,
    color: 'primary',
    icon: 'bi-building',
  },
  {
    title: 'Average Revenue',
    value: '$75M',
    trend: 12.5,
    color: 'success',
    icon: 'bi-currency-dollar',
  },
  {
    title: 'Market Growth',
    value: '12.5%',
    trend: 12.5,
    color: 'warning',
    icon: 'bi-graph-up',
  },
  {
    title: 'Top Industries',
    value: ['Technology', 'Manufacturing', 'Healthcare', 'Retail'],
    trend: 12.5,
    color: 'info',
    icon: 'bi-bar-chart',
  },
];

// Sektör listesi
const industries = [
  'Technology',
  'Manufacturing',
  'Healthcare',
  'Retail',
  'Finance',
  'Education',
  'Energy',
  'Transportation',
];

const CompanyResearchPage: React.FC = () => {
  const [showNewCompanyModal, setShowNewCompanyModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Şirketleri filtreleme
  const filteredCompanies = dummyCompanies.filter(
    company =>
      company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.industry.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Modal işleyicileri
  const handleCloseModal = () => setShowNewCompanyModal(false);
  const handleShowModal = () => setShowNewCompanyModal(true);

  return (
    <Container fluid className="marketing-dashboard company-research">
      {/* Başlık Bölümü */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-info">
            <div className="header-icon">
              <i className="bi bi-building-fill"></i>
            </div>
            <div className="header-text">
              <h1 className="page-title">Company Research</h1>
              <p className="page-subtitle">
                Analyze and track company profiles, market trends, and competitor insights
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Arama ve Filtre Bölümü */}
      <Card className="content-card mb-4">
        <Card.Body className="card-body-custom">
          <Row className="align-items-center g-3">
            <Col xs={12} md={6}>
              <Input
                placeholder="Search companies..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                leftIcon={<i className="bi bi-search"></i>}
              />
            </Col>
            <Col xs={12} md={6} className="text-md-end">
              <Button
                variant="primary"
                onClick={handleShowModal}
                leftIcon={<i className="bi bi-plus-lg"></i>}
                style={{ color: '#fff' }}
              >
                Add Company
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Pazar Genel Bakış Kartları */}
      <Row className="mb-4 g-3">
        {marketAnalysis.map((metric, index) => (
          <Col xs={12} sm={6} md={3} key={index}>
            <div className="market-metric-card">
              <div className="d-flex align-items-center mb-3">
                <div className={`metric-icon ${metric.color}`}>
                  <i className={metric.icon}></i>
                </div>
                <h6 className="fw-bold mb-0" style={{ color: "var(--text-primary)" }}>{metric.title}</h6>
              </div>

              <div className="metric-value">
                {Array.isArray(metric.value)
                  ? metric.value.slice(0, 2).join(', ') + (metric.value.length > 2 ? '...' : '')
                  : metric.value}
              </div>

              <div className={`metric-trend ${metric.trend > 0 ? 'positive' : 'negative'}`}>
                <i className={`bi ${metric.trend > 0 ? 'bi-graph-up-arrow' : 'bi-graph-down-arrow'}`}></i>
                <span>{Math.abs(metric.trend)}% from last month</span>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Şirket Listesi */}
      <Card className="content-card">
        <Card.Body className="p-0">
          <div className="company-list">
            {filteredCompanies.length > 0 ? (
              filteredCompanies.map((company) => (
                <div key={company.id} className="company-list-item">
                  <div className="d-flex flex-column flex-md-row">
                    <div className="d-flex align-items-center mb-3 mb-md-0">
                      <div className="company-icon me-3">
                        <i className="bi bi-building"></i>
                      </div>
                    </div>

                    <div className="flex-grow-1">
                      <h5 className="fw-bold mb-2" style={{ color: "var(--text-primary)" }}>{company.name}</h5>
                      <p className="text-muted mb-2">{company.description}</p>
                      <div className="d-flex flex-wrap gap-2">
                        <span className="badge bg-light text-dark border d-flex align-items-center">
                          <i className="bi bi-building me-1"></i> {company.industry}
                        </span>
                        <span className="badge bg-light text-dark border d-flex align-items-center">
                          <i className="bi bi-people me-1"></i> {company.employees} employees
                        </span>
                        <span className="badge bg-light text-dark border d-flex align-items-center">
                          <i className="bi bi-geo-alt me-1"></i> {company.location}
                        </span>
                      </div>
                    </div>

                    <div className="d-flex mt-3 mt-md-0 align-items-start gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        iconOnly
                        style={{ color: '#521DB3', borderColor: '#521DB3' }}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        iconOnly
                        style={{ color: '#fff' }}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-5">
                <p className="text-muted mb-0">No companies found matching your search criteria.</p>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Yeni Şirket Ekleme Modal */}
      <Modal
        show={showNewCompanyModal}
        onHide={handleCloseModal}
        centered
        contentClassName="bg-transparent border-0"
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">Add New Company</h5>
            <button
              type="button"
              className="btn-close"
              style={{ filter: isDark ? 'invert(1)' : 'none' }}
              onClick={handleCloseModal}
            ></button>
          </div>
          <div className="modal-body p-4">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Company Name</Form.Label>
                <Input placeholder="Enter company name" />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Industry</Form.Label>
                <Form.Select className="form-select">
                  <option value="">Select an industry</option>
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter company description"
                  className="form-control"
                />
              </Form.Group>

              <Row>
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Location</Form.Label>
                    <Input placeholder="City, State" />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Employees</Form.Label>
                    <Input type="number" placeholder="Number of employees" />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </div>
          <div className="modal-footer">
            <Button variant="ghost" onClick={handleCloseModal} style={{ color: 'var(--text-secondary)' }}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCloseModal}
              style={{ color: '#fff' }}
            >
              Add Company
            </Button>
          </div>
        </div>
      </Modal>
    </Container>
  );
};

export default CompanyResearchPage;
